import { createSignal } from 'solid-js';
import { pushEvent } from '~/lib/event-log';

export type WakeLockStatus = 'unsupported' | 'idle' | 'held' | 'released' | 'denied' | 'error';

/** Screens that can claim the wake lock. The geolocation singleton uses
 *  the same `'map' | 'diag'` taxonomy for its `startedBy` field; keep
 *  them aligned. */
export type WakeLockOwner = 'map' | 'diag';

interface WakeLockSentinelLike {
  released: boolean;
  type: string;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
  removeEventListener: (type: 'release', listener: () => void) => void;
}

const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

const [status, setStatus] = createSignal<WakeLockStatus>(supported ? 'idle' : 'unsupported');
const [owners, setOwners] = createSignal<readonly WakeLockOwner[]>([]);
const [heldSince, setHeldSince] = createSignal<number | null>(null);
const [totalHeldMs, setTotalHeldMs] = createSignal(0);
const [lastError, setLastError] = createSignal<string | null>(null);
const [reacquireAttempts, setReacquireAttempts] = createSignal(0);

let sentinel: WakeLockSentinelLike | null = null;
let onSentinelRelease: (() => void) | null = null;
/** Set true immediately before a user-initiated `release()` so the
 *  sentinel's `release` event can be classified correctly. WebKit auto-
 *  releases on hidden, which arrives with this flag still false. */
let pendingUserRelease = false;

function flushHeldTime(): void {
  const since = heldSince();
  if (since !== null) {
    setTotalHeldMs((m) => m + (Date.now() - since));
    setHeldSince(null);
  }
}

async function requestSentinel(): Promise<boolean> {
  if (!supported) return false;
  const wl = (
    navigator as Navigator & {
      wakeLock?: { request: (t: 'screen') => Promise<WakeLockSentinelLike> };
    }
  ).wakeLock;
  if (!wl) {
    setStatus('unsupported');
    return false;
  }
  try {
    const s = await wl.request('screen');
    sentinel = s;
    setHeldSince(Date.now());
    setStatus('held');
    setLastError(null);
    // The closure below is bound to the sentinel's `release` event
    // (a platform event-handler context). The lint rule sees only a
    // function literal containing reactivity reads and can't trace the
    // event-listener registration, so disable per-line.
    // eslint-disable-next-line solid/reactivity
    onSentinelRelease = () => {
      flushHeldTime();
      if (sentinel === s) {
        sentinel = null;
        const reason: 'user' | 'auto' = pendingUserRelease ? 'user' : 'auto';
        pendingUserRelease = false;
        pushEvent('wake_lock_released', { reason });
        // If still wanted (set non-empty), mark released pending re-acquire
        // when foreground returns. Otherwise back to idle.
        setStatus(owners().length > 0 ? 'released' : 'idle');
      }
    };
    s.addEventListener('release', onSentinelRelease);
    return true;
  } catch (err) {
    const name = (err as Error)?.name ?? '';
    const msg = (err as Error)?.message ?? String(err);
    setLastError(`${name || 'Error'}: ${msg}`);
    setStatus(name === 'NotAllowedError' ? 'denied' : 'error');
    return false;
  }
}

/** Acquire the wake lock on behalf of `by`. Adds the caller to the
 *  owner set and ensures the OS sentinel is held. Idempotent: calling
 *  twice with the same `by` is a no-op past the first call. */
async function acquire(by: WakeLockOwner): Promise<void> {
  setOwners((list) => (list.includes(by) ? list : [...list, by]));
  if (!supported) return;
  if (status() === 'held' && sentinel && !sentinel.released) return;
  const ok = await requestSentinel();
  if (ok) pushEvent('wake_lock_acquired', { by });
}

/** Release the wake lock on behalf of `by`. Removes the caller from
 *  the owner set; releases the OS sentinel only when the set is empty.
 *  Safe to call when `by` isn't currently in the set. */
async function release(by: WakeLockOwner): Promise<void> {
  setOwners((list) => list.filter((o) => o !== by));
  if (owners().length > 0) return;
  if (sentinel && !sentinel.released) {
    pendingUserRelease = true;
    try {
      await sentinel.release();
    } catch {
      pendingUserRelease = false;
      // Sentinel was already gone; nothing to do.
    }
  }
  flushHeldTime();
  sentinel = null;
  setStatus(supported ? 'idle' : 'unsupported');
}

if (typeof document !== 'undefined') {
  // Module-level listener: WebKit releases the sentinel when the
  // document hides and does not auto-restore it on return. When the
  // tab comes back to foreground AND someone still wants the lock,
  // request a fresh sentinel.
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible' &&
      owners().length > 0 &&
      (status() === 'released' || status() === 'error')
    ) {
      setReacquireAttempts((n) => n + 1);
      void requestSentinel().then((ok) => {
        if (ok) pushEvent('wake_lock_reacquired');
      });
    }
  });
}

/** Module-level wake-lock manager. Ref-counted across MAP and DIAG so
 *  tab-switching between the two never churns the OS sentinel. The
 *  internal Set-of-owners pattern mirrors the geolocation singleton's
 *  `startedBy`; see prompt 08 for the rationale. */
export const wakeLock = {
  acquire,
  release,
  status,
  owners,
  desired: () => owners().length > 0,
  held: () => status() === 'held',
  heldSince,
  totalHeldMs,
  lastError,
  reacquireAttempts,
};
